/**
 * @userstory US-STRIPE-01, US-STRIPE-02, US-STRIPE-03, US-SUB-01, US-SUB-03, US-SUB-04, US-SUB-05
 *
 * API client for subscription management endpoints.
 * Provides methods for viewing subscriptions (US-STRIPE-01 / US-SUB-01),
 * generating Stripe Customer Portal URLs (US-STRIPE-02 / US-SUB-03),
 * and synchronizing subscription state with Stripe (US-STRIPE-03).
 */
import apiClient from './apiClient';

// ============================================================================
// Types
// ============================================================================

/**
 * Response from GET /api/organizations/{id}/subscriptions/portal
 * or GET /api/talents/{id}/subscriptions/portal
 */
export interface PortalResponse {
	url: string;
}

export interface PortalErrorResponse {
	code: string;
	messageKey: string;
}

/**
 * Response from GET /api/organizations/{id}/subscriptions/current
 * or GET /api/talents/{id}/subscriptions/current
 */
export interface CurrentSubscription {
	plan: string; // "Free" | "Plus" | "Pro" | "Enterprise" (Plus=Startup, Pro=Business/Talent Pro)
	status: string; // "Active" | "Canceled" | "PastDue"
	isPaid: boolean;
	currentPeriodStart: string | null;
	currentPeriodEnd: string | null;
	cancelAtPeriodEnd: boolean;
	maxActiveQuotes: number;
	activeQuotesCount: number;
	remainingQuotes: number;
	canCreateQuote: boolean;
	usagePercentage: number;
}

export type SubscriptionDashboardState =
	| 'active'
	| 'available'
	| 'requires_paid'
	| 'enterprise_only'
	| 'deferred'
	| 'locked'
	| 'exhausted';

export const SubscriptionEntitlementCodes = {
	companyFree: 'COMPANY_FREE',
	companyPaid: 'COMPANY_PAID',
	companyProEnterprise: 'COMPANY_PRO_ENTERPRISE',
	talentFree: 'TALENT_FREE',
	talentPaid: 'TALENT_PAID',
	addOnCompanyChatExtra: 'addon.chat_extra',
	addOnCompanyFreelanceManagement: 'addon.freelance_management',
} as const;

export interface SubscriptionPackageSummary {
	plan: string;
	entitlementCode: string;
	labelKey: string;
	status: string;
	statusKey: string;
	isPaid: boolean;
	isEnterprise: boolean;
	currentPeriodStart: string | null;
	currentPeriodEnd: string | null;
	cancelAtPeriodEnd: boolean;
	grandfatheredActive: boolean;
}

export interface SubscriptionRoleState {
	canManageSubscription: boolean;
	isReadOnly: boolean;
	reasonCode?: string | null;
}

export interface SubscriptionDashboardAction {
	code: string;
	labelKey: string;
	kind: string;
	isEnabled: boolean;
	disabledReasonCode?: string | null;
}

export interface SubscriptionUsageCard {
	code: string;
	labelKey: string;
	descriptionKey?: string | null;
	used?: number | null;
	limit?: number | null;
	remaining?: number | null;
	isUnlimited: boolean;
	isOverflow: boolean;
	state: SubscriptionDashboardState;
	requiredEntitlementCode?: string | null;
}

export interface SubscriptionAddOnCard {
	code: string;
	entitlementCode: string;
	labelKey: string;
	descriptionKey?: string | null;
	state: SubscriptionDashboardState;
	selfService: boolean;
	requiresEntitlementCode?: string | null;
	action: SubscriptionDashboardAction;
}

export interface SubscriptionDashboardWarning {
	code: string;
	severity: string;
	messageKey: string;
}

// ─── Pricing Catalog ────────────────────────────────────────────────────────

/** @userstory US-CSUB-09 */
export interface PlanCardCapability {
	key: string;
	included: boolean;
}

/** @userstory US-CSUB-09, US-CSUB-14 */
export interface PlanCardQuota {
	key: string;
	monthlyLimit: number | null;
	isUnlimited: boolean;
	/** 'per_seat' (pool scales with active seats) | 'per_org' (single flat allowance). */
	scope: string;
}

/** @userstory US-CSUB-08 */
export interface PlanCardDto {
	planKey: string;
	/** Numeric amount sourced live from Stripe; null when unavailable. The frontend formats it. */
	priceAmount: number | null;
	isStartingPrice?: boolean;
	currency: string;
	/** Stripe recurring interval, e.g. 'month' | 'year'. */
	billingInterval: string;
	ctaKind: string;
	capabilities: PlanCardCapability[];
	quotas: PlanCardQuota[];
}

/** @userstory US-CSUB-10 */
export interface ChatExtraCardDto {
	activePackCount: number;
	totalBonusChats: number;
	priceAmount: number | null;
	currency: string;
	bonusPerPack: number;
	ctaVariant: string; // 'add' | 'manage'
}

/** @userstory US-FM-03, US-FM-07, US-CSUB-10 */
export interface FreelanceManagementCardDto {
	isActive: boolean;
	priceAmount: number | null;
	yearlyPriceAmount: number | null;
	currency: string;
	ctaVariant: string; // 'add' | 'manage'
}

/** @userstory US-CSUB-08, US-CSUB-09, US-CSUB-10 */
export interface SubscriptionCatalogDto {
	plans: PlanCardDto[];
	chatExtra: ChatExtraCardDto | null;
	freelanceManagement: FreelanceManagementCardDto | null;
}

// ─── Current Usage ──────────────────────────────────────────────────────────

/** @userstory US-CSUB-12, US-CSUB-13 */
export interface QuotaUsageItem {
	key: string;
	used: number;
	monthlyLimit: number | null;
	isUnlimited: boolean;
	/** @userstory US-CSUB-13 True when the limit is a per-seat pool (perSeatBase × activeSeats [+ addOnBonus]). */
	isPooled?: boolean;
	/** @userstory US-CSUB-13 Per-seat base allotment for a pooled quota; null otherwise. */
	perSeatBase?: number | null;
	/** @userstory US-CSUB-13 Number of active seats the pool is multiplied by (>= 1). */
	activeSeats?: number;
	/** @userstory US-CSUB-13 Flat add-on bonus added on top of the pool (e.g. Chat Extra). */
	addOnBonus?: number;
}

/** @userstory US-CSUB-12 */
export interface CurrentUsage {
	quotas: QuotaUsageItem[];
	/** @userstory US-COM-FG-25 True when the organization is on the premium AI model tier. */
	premiumAiModel?: boolean;
}

// ─── Dashboard ───────────────────────────────────────────────────────────────

export interface SubscriptionDashboard {
	audience: 'company' | 'talent';
	package: SubscriptionPackageSummary;
	roleState: SubscriptionRoleState;
	primaryAction: SubscriptionDashboardAction;
	usageCards: SubscriptionUsageCard[];
	addOns: SubscriptionAddOnCard[];
	warnings: SubscriptionDashboardWarning[];
	/** Pricing catalog — present only for company dashboard */
	catalog?: SubscriptionCatalogDto | null;
	/** Current-period usage for the plan's quotas — present only for company dashboard */
	currentUsage?: CurrentUsage | null;
}

/**
 * Append-only history item.
 * @userstory US-CSUB-01, US-TSUB-01
 */
export interface SubscriptionHistoryItem {
	id: string;
	plan: string;
	status: string;
	periodStart: string;
	periodEnd: string;
	createdAt: string;
}

// ============================================================================
// Service
// ============================================================================

const subscriptionService = {
	/**
	 * Gets the Stripe Customer Portal URL for an organization.
	 * @param organizationId - The organization ID
	 * @param returnUrl - URL to redirect back to after portal session
	 * @returns The Stripe portal URL
	 */
	getOrganizationPortalUrl: async (
		organizationId: string,
		returnUrl?: string,
	): Promise<string> => {
		const params = new URLSearchParams();
		if (returnUrl) {
			params.append('returnUrl', returnUrl);
		}
		const queryString = params.toString();
		const url = `/api/organizations/${organizationId}/subscriptions/portal${queryString ? `?${queryString}` : ''}`;

		const response = await apiClient.get<PortalResponse>(url);
		return response.data.url;
	},

	/**
	 * Gets the Stripe Customer Portal URL for a talent.
	 * @param talentId - The talent ID
	 * @param returnUrl - URL to redirect back to after portal session
	 * @returns The Stripe portal URL
	 */
	getTalentPortalUrl: async (talentId: string, returnUrl?: string): Promise<string> => {
		const params = new URLSearchParams();
		if (returnUrl) {
			params.append('returnUrl', returnUrl);
		}
		const queryString = params.toString();
		const url = `/api/talentusers/me/subscriptions/portal${queryString ? `?${queryString}` : ''}`;

		const response = await apiClient.get<PortalResponse>(url);
		return response.data.url;
	},

	/**
	 * Gets current subscription details for an organization.
	 * @param organizationId - The organization ID
	 * @returns Current subscription data
	 */
	getOrganizationSubscription: async (organizationId: string): Promise<CurrentSubscription> => {
		const response = await apiClient.get<CurrentSubscription>(
			`/api/organizations/${organizationId}/subscriptions/current`,
		);
		return response.data;
	},

	/** @userstory US-CSUB-01, US-CSUB-02, US-CSUB-04, US-CSUB-06 */
	getOrganizationSubscriptionDashboard: async (
		organizationId: string,
	): Promise<SubscriptionDashboard> => {
		const response = await apiClient.get<SubscriptionDashboard>(
			`/api/organizations/${organizationId}/subscriptions/dashboard`,
		);
		return response.data;
	},

	/**
	 * Syncs the organization's subscription with Stripe.
	 * Creates a Free subscription if none exists.
	 * Should be called before accessing subscription data or portal.
	 * @param organizationId - The organization ID
	 * @returns Updated subscription data
	 */
	syncOrganizationSubscription: async (organizationId: string): Promise<CurrentSubscription> => {
		const response = await apiClient.post<CurrentSubscription>(
			`/api/organizations/${organizationId}/subscriptions/sync`,
		);
		return response.data;
	},

	/**
	 * Gets current subscription details for a talent.
	 * @param talentId - The talent ID
	 * @returns Current subscription data
	 */
	getTalentSubscription: async (_talentId: string): Promise<CurrentSubscription> => {
		void _talentId;
		const response = await apiClient.get<CurrentSubscription>(
			`/api/talentusers/me/subscriptions/current`,
		);
		return response.data;
	},

	/** @userstory US-TSUB-01, US-TSUB-02, US-TSUB-04, US-TSUB-05, US-TSUB-08, US-TSUB-10 */
	getTalentSubscriptionDashboard: async (): Promise<SubscriptionDashboard> => {
		const response = await apiClient.get<SubscriptionDashboard>(
			`/api/talentusers/me/subscriptions/dashboard`,
		);
		return response.data;
	},

	/**
	 * Syncs the talent's subscription with Stripe.
	 * Creates a Free subscription if none exists.
	 * Should be called before accessing subscription data or portal.
	 * @param talentId - The talent ID
	 * @returns Updated subscription data
	 */
	syncTalentSubscription: async (_talentId: string): Promise<CurrentSubscription> => {
		void _talentId;
		const response = await apiClient.post<CurrentSubscription>(
			`/api/talentusers/me/subscriptions/sync`,
		);
		return response.data;
	},

	/**
	 * Redirects user to Stripe Customer Portal for an organization.
	 * This performs a full page redirect.
	 * @param organizationId - The organization ID
	 * @param returnUrl - URL to redirect back to (defaults to current page)
	 */
	redirectToOrganizationPortal: async (
		organizationId: string,
		returnUrl?: string,
	): Promise<void> => {
		const url = await subscriptionService.getOrganizationPortalUrl(
			organizationId,
			returnUrl ?? window.location.href,
		);
		window.location.href = url;
	},

	/**
	 * Redirects talent to Stripe Customer Portal.
	 * @param talentId - The talent ID
	 * @param returnUrl - URL to redirect back to (defaults to current page)
	 */
	redirectToTalentPortal: async (talentId: string, returnUrl?: string): Promise<void> => {
		const url = await subscriptionService.getTalentPortalUrl(
			talentId,
			returnUrl ?? window.location.href,
		);
		window.location.href = url;
	},

	/**
	 * Activates the talent's episodic Active plan (or resumes a paused one).
	 * @userstory US-TSUB-09
	 * @returns Updated subscription dashboard
	 */
	activateTalentSubscription: async (): Promise<SubscriptionDashboard> => {
		const response = await apiClient.post<SubscriptionDashboard>(
			`/api/talentusers/me/subscriptions/activate`,
		);
		return response.data;
	},

	/**
	 * Pauses the talent's Active episodic plan.
	 * @userstory US-TSUB-09
	 * @returns Updated subscription dashboard
	 */
	pauseTalentSubscription: async (): Promise<SubscriptionDashboard> => {
		const response = await apiClient.post<SubscriptionDashboard>(
			`/api/talentusers/me/subscriptions/pause`,
		);
		return response.data;
	},

	/**
	 * Resumes the talent's paused Active episodic plan.
	 * @userstory US-TSUB-09
	 * @returns Updated subscription dashboard
	 */
	resumeTalentSubscription: async (): Promise<SubscriptionDashboard> => {
		const response = await apiClient.post<SubscriptionDashboard>(
			`/api/talentusers/me/subscriptions/resume`,
		);
		return response.data;
	},

	/**
	 * Gets the append-only subscription history for an organization.
	 * Used by the subscription page history drawer (lazy-loaded on open).
	 * @userstory US-CSUB-01
	 */
	getOrganizationSubscriptionHistory: async (
		organizationId: string,
	): Promise<SubscriptionHistoryItem[]> => {
		const response = await apiClient.get<SubscriptionHistoryItem[]>(
			`/api/organizations/${organizationId}/subscriptions/history`,
		);
		return response.data;
	},

	/**
	 * Gets the append-only subscription history for the current talent.
	 * @userstory US-TSUB-01
	 */
	getTalentSubscriptionHistory: async (): Promise<SubscriptionHistoryItem[]> => {
		const response = await apiClient.get<SubscriptionHistoryItem[]>(
			`/api/talentusers/me/subscriptions/history`,
		);
		return response.data;
	},
};

export default subscriptionService;
